/**
 * PRDs Listing Page
 *
 * View all PRDs across repositories
 */

import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Button,
} from '@forgenetwork/ui';
import { api } from '@/lib/api';
import { useWorkspaces } from '@/features/workspace/api';
import {
  FileText,
  Plus,
  Clock,
  GitBranch,
  AlertCircle,
  Loader2,
} from 'lucide-react';

export const Route = createFileRoute('/_authenticated/prds')({
  component: PRDsPage,
});

interface PRD {
  id: string;
  title: string;
  description: string | null;
  status: 'draft' | 'review' | 'approved' | 'archived';
  version: string;
  repositoryId: string;
  repositoryName?: string;
  createdAt: string;
  updatedAt: string;
}

interface PRDsResponse {
  prds: PRD[];
  total: number;
}

function PRDsPage(): React.JSX.Element {
  const { data: workspacesData } = useWorkspaces();
  const workspaces = React.useMemo(() => {
    if (!workspacesData) return [];
    if (Array.isArray(workspacesData)) return workspacesData;
    if (workspacesData && typeof workspacesData === 'object' && 'workspaces' in workspacesData) {
      const ws = (workspacesData as { workspaces?: unknown }).workspaces;
      return Array.isArray(ws) ? ws : [];
    }
    return [];
  }, [workspacesData]);

  const workspaceId = workspaces[0]?.id;

  const { data, isLoading, error } = useQuery<PRDsResponse>({
    queryKey: ['prds', workspaceId],
    queryFn: async () => {
      if (!workspaceId) {
        // Return mock data if no workspace
        return {
          prds: [],
          total: 0,
        };
      }
      try {
        return await api.get<PRDsResponse>(`/workspaces/${workspaceId}/prds`);
      } catch {
        // Return empty if endpoint doesn't exist
        return { prds: [], total: 0 };
      }
    },
    enabled: true,
  });

  const prds = data?.prds ?? [];

  const getStatusBadge = (status: PRD['status']) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500">Approved</Badge>;
      case 'review':
        return <Badge className="bg-yellow-500">In Review</Badge>;
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'archived':
        return <Badge variant="outline">Archived</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!workspaceId || isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Error Loading PRDs
            </CardTitle>
            <CardDescription>
              {error instanceof Error ? error.message : 'Failed to load PRDs'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Product Requirements</h1>
            <p className="text-muted-foreground mt-1">
              Manage and track all your PRDs across repositories
            </p>
          </div>
          <Button asChild>
            <Link to="/repositories">
              <Plus className="h-4 w-4 mr-2" />
              Create PRD
            </Link>
          </Button>
        </div>

        {prds.length === 0 ? (
          <Card>
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-4">
                <FileText className="h-16 w-16 text-muted-foreground" />
              </div>
              <CardTitle>No PRDs yet</CardTitle>
              <CardDescription>
                Create your first PRD by analyzing a repository
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center pb-8">
              <Button asChild>
                <Link to="/repositories">
                  <Plus className="h-4 w-4 mr-2" />
                  Go to Repositories
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {prds.map((prd) => (
              <Card key={prd.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <Link
                        to="/prd/$prdId"
                        params={{ prdId: prd.id }}
                        className="hover:underline"
                      >
                        <CardTitle className="text-lg truncate">{prd.title}</CardTitle>
                      </Link>
                      {prd.repositoryName && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                          <GitBranch className="h-3 w-3" />
                          <span>{prd.repositoryName}</span>
                        </div>
                      )}
                    </div>
                    {getStatusBadge(prd.status)}
                  </div>
                  {prd.description && (
                    <CardDescription className="line-clamp-2 mt-2">
                      {prd.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{formatDate(prd.updatedAt)}</span>
                    </div>
                    <span>v{prd.version}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
